import { useState, useMemo, useRef } from "react";
import { Search, Loader2 } from "lucide-react";
import { useHandleAutocomplete } from "@/hooks/useHandleAutocomplete";
import type { Influencer } from "@/types/influencer";
import type { LeaderboardEntry } from "@/hooks/useLeaderboard";

interface UnifiedSearchBarProps {
  influencers: (Influencer | LeaderboardEntry)[];
  onSearch: (query: string) => void;
  onNavigateToProfile?: (handle: string) => void;
}

const UnifiedSearchBar = ({ influencers, onSearch, onNavigateToProfile }: UnifiedSearchBarProps) => {
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleFound = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^@/, "");
    if (!q) return true;
    return influencers.some(
      (i) =>
        i.handle.toLowerCase().replace(/^@/, "") === q ||
        i.displayName.toLowerCase() === q
    );
  }, [query, influencers]);

  const { results: autocompleteResults, isLoading: isAutocompleteLoading, clear: clearAutocomplete } =
    useHandleAutocomplete(query, !handleFound);

  const handleChange = (value: string) => {
    setQuery(value);
    onSearch(value);
    setShowDropdown(true);
  };

  const navigateToHandle = (screenName: string) => {
    const clean = screenName.replace(/^@/, "");
    // Check if already in leaderboard
    const alreadyTracked = influencers.some(
      (i) => i.handle.toLowerCase().replace(/^@/, "") === clean.toLowerCase()
    );
    if (alreadyTracked) {
      onNavigateToProfile?.(clean);
    } else {
      // Navigate immediately with analyzing flag — fetch-tweets runs from ProfilePage
      onNavigateToProfile?.(`${clean}?analyzing=1`);
    }
    setQuery("");
    onSearch("");
    setShowDropdown(false);
    clearAutocomplete();
  };

  const handleSelectSuggestion = (screenName: string) => {
    navigateToHandle(screenName);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!handleFound && query.trim()) {
      navigateToHandle(query.trim());
    }
  };

  // Close dropdown on outside click
  const handleBlur = (e: React.FocusEvent) => {
    if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
      setTimeout(() => setShowDropdown(false), 150);
    }
  };

  const showSuggestions = showDropdown && autocompleteResults.length > 0;

  return (
    <div ref={wrapperRef} className="relative max-w-md w-full scroll-mt-20" onBlur={handleBlur}>
      <form onSubmit={handleSubmit} className="relative group">
        <div className="absolute inset-0 rounded-lg bg-primary/10 blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" />
        <div className="relative flex items-center bg-secondary border border-border rounded-lg px-4 py-3 focus-within:glow-border transition-all duration-300">
          {isAutocompleteLoading ? (
            <Loader2 className="w-4 h-4 text-primary mr-3 shrink-0 animate-spin" />
          ) : (
            <Search className="w-4 h-4 text-muted-foreground mr-3 shrink-0" />
          )}
          <input
            type="text"
            placeholder="Search @handle or name..."
            value={query}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={() => {
              setShowDropdown(true);
              setTimeout(() => {
                wrapperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 100);
            }}
            className="bg-transparent text-foreground placeholder:text-muted-foreground outline-none w-full font-mono text-sm"
          />
          <button
            type="submit"
            disabled={!query.trim() || handleFound}
            className={`ml-2 px-3 py-1 rounded font-mono text-xs font-bold transition-all shrink-0 ${
              !handleFound && query.trim()
                ? "bg-profit text-primary-foreground hover:opacity-90"
                : "bg-primary/20 text-muted-foreground opacity-40 cursor-not-allowed"
            }`}
          >
            Analyze
          </button>
        </div>
      </form>

      {/* Autocomplete Dropdown */}
      {showSuggestions && (
        <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {autocompleteResults.map((user) => (
            <button
              key={user.screen_name}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectSuggestion(user.screen_name)}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary transition-colors text-left"
            >
              {user.profile_image_url ? (
                <img
                  src={user.profile_image_url}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-muted shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm text-foreground truncate">
                  @{user.screen_name}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {user.name}
                  {user.followers_count > 0 && (
                    <span className="ml-2">
                      {user.followers_count >= 1000
                        ? `${(user.followers_count / 1000).toFixed(1)}K`
                        : user.followers_count}{" "}
                      followers
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnifiedSearchBar;
